import React, { useEffect, useRef, useState } from 'react';
import { Input, Radio } from 'antd';
import { orderHomes } from 'utils/helpers';
import { SLH_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';
import { IconSoberLiving, IconChild, IconNoRelation, IconSmallArrow, IconSearch } from 'components';

const SlhSelect = ({
  data = [],
  value,
  onChange,
  onSearch,
  placeholder = 'SLH',
  placement = 'bottom',
  isWithAll = false,
  disabled = false,
}) => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const ordered = orderHomes(Array.isArray(data) ? data : []);
    setList(ordered);
    setFiltered(ordered);
  }, [data]);

  useEffect(() => {
    if (value === null || value === undefined) {
      setIsOpen(false);
      setSearchValue('');
      setFiltered(list);
    }
  }, [list, value]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setFiltered(list);
    }
  }, [isOpen, list]);

  const handleSearch = (e) => {
    const term = e.target.value || '';
    if (onSearch) onSearch(e);
    if (!term) {
      setFiltered(list);
      setSearchValue('');
      return;
    }
    setFiltered(list.filter((p) => p.name.toLowerCase().includes(term.toLowerCase())));
    setSearchValue(term);
  };

  const handleChange = (e) => {
    const id = e.target.value;
    const normalized = id === null || id === 'null' ? null : id;
    if (onChange) onChange(normalized);
    setIsOpen(false);
    setSearchValue('');
    setFiltered(list);
  };

  const selectedLabel = () => {
    if (value === null) return 'All';
    if (value === undefined || value === '') return placeholder;
    const found = list.find((p) => String(p.id) === String(value));
    return found ? found.name : placeholder;
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative z-50 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div
        className="flex items-center h-[40px] px-3 gap-x-2 text-base font-medium base-block rounded-lg border border-color-divider cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flex-1">{selectedLabel()}</span>
        <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
      </div>

      <div
        className={`flex flex-col rounded-xl bg-white shadow-block-shadow p-4 gap-y-2 w-[450px] absolute left-0 ${placement === 'bottom' ? 'top-[calc(100%+16px)]' : 'bottom-[calc(100%+16px)]'} ${isOpen ? 'block' : 'hidden'}`}
      >
        <Input
          className="search text-lightBlue text-16 rounded-ten bg-light"
          type="text"
          placeholder="Search"
          addonBefore={<IconSearch color={COLORS.lightBlue} />}
          value={searchValue}
          onChange={handleSearch}
        />

        <div className="flex flex-col bg-light border border-divider rounded-lg max-h-[450px] overflow-y-auto">
          <Radio.Group
            onChange={handleChange}
            className="w-full flex flex-col gap-x-4"
            value={value === null ? null : +value}
          >
            {isWithAll && (
              <Radio
                className={`px-4 py-1.5 m-0 flex-1 flex items-center rounded-lg hover:bg-lightBlue ${value === null ? '!bg-white border border-orange' : ''}`}
                value={null}
              >
                <div className="flex items-center">
                  <p className="text-sm whitespace-nowrap">All</p>
                </div>
              </Radio>
            )}
            {filtered?.length > 0 &&
              Array.isArray(filtered) &&
              filtered.map((slh) => (
                <Radio
                  key={slh.id}
                  className={`${slh.type === SLH_TYPES.child ? 'ml-6' : ''} px-4 py-2 m-0 flex-1 flex items-center rounded-lg hover:bg-lightBlue ${String(value) === String(slh.id) ? '!bg-white border border-orange' : ''}`}
                  value={slh.id}
                >
                  <div className="flex items-center">
                    {slh.type === SLH_TYPES.parent ? (
                      <IconSoberLiving className="mr-2" />
                    ) : slh.type === SLH_TYPES.child ? (
                      <IconChild className="mr-2" />
                    ) : (
                      <IconNoRelation className="mr-2" />
                    )}
                    <p className="text-sm whitespace-nowrap">{slh.name}</p>
                  </div>
                </Radio>
              ))}
          </Radio.Group>
        </div>
      </div>
    </div>
  );
};

export default SlhSelect;
